/**
 * EnergyPanel Component
 *
 * Homepage energy panel with Day and Month views:
 * - Day: 24-hour combo chart (bars for kWh, lines for Fixed/Agile rates)
 * - Month: Calendar heatmap (green/red days based on Agile vs Fixed delta)
 *
 * A Week view will be added later.
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import { ArrowLeft, ChevronLeft, ChevronRight, Zap } from 'lucide-react'
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import { useDailyEnergy } from '@/hooks/useDailyEnergy'
import { useEnergySync } from '@/hooks/useEnergySync'
import MonthlyEnergyCalendar from './MonthlyEnergyCalendar'
import WeeklyEnergyChart from './WeeklyEnergyChart'

const FIXED_COLOR = '#f59e0b'   // amber
const AGILE_COLOR = '#0ea5e9'   // sky blue
const BAR_COLOR = '#a3a3a3'     // neutral-400
const SAVING_COLOR = '#22c55e'  // green
const DEFICIT_COLOR = '#ef4444' // red

const VIEW_TABS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

function toLocalDate(date) {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function toMonthLabel(date) {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

function getMonday(d) {
  const date = new Date(d)
  const day = date.getDay()
  const diff = day === 0 ? -6 : 1 - day // Monday = 1
  date.setDate(date.getDate() + diff)
  date.setHours(0, 0, 0, 0)
  return date
}

function toWeekLabel(monday) {
  const sunday = new Date(monday)
  sunday.setDate(sunday.getDate() + 6)
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  return `${fmt(monday)} – ${fmt(sunday)} ${sunday.getFullYear()}`
}

function toISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function SlotTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const hasConsumption = row.consumption_kwh != null

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-48">
      <p className="font-medium text-neutral-900 mb-2">{row.slot_label}</p>

      {hasConsumption && (
        <div className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: BAR_COLOR }} />
            <span className="text-neutral-600">Usage</span>
          </div>
          <span className="font-medium text-neutral-900">
            {parseFloat(row.consumption_kwh).toFixed(2)} kWh
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 text-sm mt-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FIXED_COLOR }} />
          <span className="text-neutral-600">Fixed</span>
        </div>
        <span className="font-medium text-neutral-900">
          {row.fixed_rate_pence != null ? `${parseFloat(row.fixed_rate_pence).toFixed(1)}p` : '—'}
        </span>
      </div>

      {row.agile_rate_pence != null && (
        <div className="flex items-center justify-between gap-4 text-sm mt-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: AGILE_COLOR }} />
            <span className="text-neutral-600">Agile</span>
          </div>
          <span className="font-medium text-neutral-900">
            {parseFloat(row.agile_rate_pence).toFixed(1)}p
          </span>
        </div>
      )}

      {row.agile_rate_pence != null && row.fixed_rate_pence != null && (
        <div className="border-t border-neutral-200 mt-2 pt-2 text-sm">
          {(() => {
            const delta = parseFloat(row.fixed_rate_pence) - parseFloat(row.agile_rate_pence)
            return (
              <span className={delta >= 0 ? 'text-green-600' : 'text-red-600'}>
                {delta >= 0
                  ? `${delta.toFixed(1)}p cheaper on Agile`
                  : `${Math.abs(delta).toFixed(1)}p dearer on Agile`
                }
              </span>
            )
          })()}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Day View (inline — same as before)
// =============================================================================

function DayView({ date, refreshKey }) {
  const dateStr = useMemo(() => toISODate(date), [date])
  const { data, isLoading } = useDailyEnergy(dateStr, refreshKey)

  const chartData = useMemo(() => {
    if (!data?.length) return []
    return data.map(row => {
      const fixed = row.fixed_rate_pence != null ? parseFloat(row.fixed_rate_pence) : null
      const agile = row.agile_rate_pence != null ? parseFloat(row.agile_rate_pence) : null

      return {
        ...row,
        consumption_kwh: row.consumption_kwh != null ? parseFloat(row.consumption_kwh) : null,
        fixedRate: fixed,
        agileRate: agile,
        savingBand: agile != null && fixed != null && agile < fixed ? [agile, fixed] : null,
        deficitBand: agile != null && fixed != null && agile > fixed ? [fixed, agile] : null,
        negativeBand: agile != null ? (agile < 0 ? [agile, 0] : [0, 0]) : null,
      }
    })
  }, [data])

  const priceDomain = useMemo(() => {
    if (!chartData.length) return [0, 40]
    const prices = chartData
      .flatMap(r => [r.fixedRate, r.agileRate])
      .filter(v => v != null)
    if (!prices.length) return [0, 40]
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    return [Math.floor(Math.min(min - 2, 0)), Math.ceil(max + 2)]
  }, [chartData])

  const hasNegativeRates = chartData.some(r => r.agileRate != null && r.agileRate < 0)

  const summary = useMemo(() => {
    const slotsWithUsage = chartData.filter(r => r.consumption_kwh != null)
    if (!slotsWithUsage.length) return null

    const totalKwh = slotsWithUsage.reduce((sum, r) => sum + r.consumption_kwh, 0)
    const fixedCostPence = slotsWithUsage.reduce(
      (sum, r) => sum + r.consumption_kwh * (r.fixedRate ?? 0), 0
    )
    const slotsWithAgile = slotsWithUsage.filter(r => r.agileRate != null)
    const agileCostPence = slotsWithAgile.reduce(
      (sum, r) => sum + r.consumption_kwh * r.agileRate, 0
    )

    return {
      totalKwh,
      fixedCost: fixedCostPence / 100,
      agileCost: slotsWithAgile.length > 0 ? agileCostPence / 100 : null,
    }
  }, [chartData])

  if (isLoading) {
    return <Skeleton className="h-[280px] w-full" />
  }

  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-[280px] text-neutral-500">
        No data available for this date
      </div>
    )
  }

  return (
    <>
      {/* Summary stats */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-sm">
        {summary ? (
          <>
            <div>
              <span className="text-muted-foreground">Usage:</span>{' '}
              <span className="font-semibold">{summary.totalKwh.toFixed(1)} kWh</span>
            </div>
            <div title={`£${summary.fixedCost.toFixed(2)} excl VAT / £${(summary.fixedCost * 1.2).toFixed(2)} inc VAT`}>
              <span className="text-muted-foreground">Fixed:</span>{' '}
              <span className="font-semibold" style={{ color: FIXED_COLOR }}>
                £{summary.fixedCost.toFixed(2)}
              </span>
            </div>
            {summary.agileCost != null && (
              <>
                <div title={`£${summary.agileCost.toFixed(2)} excl VAT / £${(summary.agileCost * 1.2).toFixed(2)} inc VAT`}>
                  <span className="text-muted-foreground">Agile:</span>{' '}
                  <span className="font-semibold" style={{ color: AGILE_COLOR }}>
                    £{summary.agileCost.toFixed(2)}
                  </span>
                </div>
                <span className="text-border">|</span>
                <div title={`£${Math.abs(summary.fixedCost - summary.agileCost).toFixed(2)} excl VAT / £${(Math.abs(summary.fixedCost - summary.agileCost) * 1.2).toFixed(2)} inc VAT`}>
                  <span className="text-muted-foreground">Saving:</span>{' '}
                  <span className={`font-semibold ${summary.agileCost <= summary.fixedCost ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.agileCost <= summary.fixedCost
                      ? `£${(summary.fixedCost - summary.agileCost).toFixed(2)}`
                      : `-£${(summary.agileCost - summary.fixedCost).toFixed(2)}`
                    }
                  </span>
                </div>
              </>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">Prices only — no consumption data for this date</span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

          <XAxis
            dataKey="slot_label"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            ticks={chartData.filter((_, i) => i % 4 === 0).map(r => r.slot_label)}
          />

          <YAxis
            yAxisId="kwh"
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => `${v}`}
            label={{
              value: 'kWh',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fontSize: 11, fill: '#9ca3af' }
            }}
          />

          <YAxis
            yAxisId="price"
            orientation="right"
            domain={priceDomain}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(v) => `${v}p`}
          />

          <Tooltip content={<SlotTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
            iconType="line"
          />

          {hasNegativeRates && (
            <ReferenceLine
              yAxisId="price"
              y={0}
              stroke="#374151"
              strokeWidth={1.5}
              strokeDasharray="4 2"
            />
          )}

          <Area yAxisId="price" type="stepAfter" dataKey="savingBand" stroke="none" fill={SAVING_COLOR} fillOpacity={0.18} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />
          <Area yAxisId="price" type="stepAfter" dataKey="deficitBand" stroke="none" fill={DEFICIT_COLOR} fillOpacity={0.18} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />
          <Area yAxisId="price" type="stepAfter" dataKey="negativeBand" stroke="none" fill={SAVING_COLOR} fillOpacity={0.4} isAnimationActive={false} activeDot={false} legendType="none" connectNulls={false} />

          <Bar yAxisId="kwh" dataKey="consumption_kwh" name="Usage (kWh)" fill={BAR_COLOR} fillOpacity={0.5} radius={[2, 2, 0, 0]} isAnimationActive={false} legendType="rect" />

          <Line yAxisId="price" type="stepAfter" dataKey="fixedRate" name="Fixed" stroke={FIXED_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line yAxisId="price" type="stepAfter" dataKey="agileRate" name="Agile" stroke={AGILE_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  )
}

// =============================================================================
// Main Panel
// =============================================================================

export default function DailyEnergyChart() {
  // Background sync — fires once on mount regardless of active tab
  const { hasNewData } = useEnergySync()

  const [viewMode, setViewMode] = useState('week')
  const [cameFromMonth, setCameFromMonth] = useState(false)

  // Day navigation
  const [date, setDate] = useState(() => new Date())
  const isToday = toISODate(date) === toISODate(new Date())

  const navigateDay = (offset) => {
    setDate(prev => {
      const next = new Date(prev)
      next.setDate(next.getDate() + offset)
      return next
    })
  }

  // Month navigation
  const [month, setMonth] = useState(() => {
    const d = new Date()
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
    return d
  })

  const isCurrentMonth = month.getFullYear() === new Date().getFullYear()
    && month.getMonth() === new Date().getMonth()

  const navigateMonth = (offset) => {
    setMonth(prev => {
      const next = new Date(prev)
      next.setMonth(next.getMonth() + offset)
      return next
    })
  }

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()))

  const isCurrentWeek = getMonday(new Date()).getTime() === weekStart.getTime()

  const navigateWeek = (offset) => {
    setWeekStart(prev => {
      const next = new Date(prev)
      next.setDate(next.getDate() + offset * 7)
      return next
    })
  }

  return (
    <Card className="border-0 shadow-none rounded-none sm:border sm:shadow-sm sm:rounded-xl">
      <CardHeader className="px-3 sm:px-6">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          <CardTitle>Energy</CardTitle>
        </div>

        <div className="flex flex-col items-center gap-2 mt-2">
          <DashboardTabs
            tabs={VIEW_TABS}
            value={viewMode}
            onValueChange={(v) => { setViewMode(v); setCameFromMonth(false) }}
            size="small"
          />

          <div className="flex items-center gap-2">
            {viewMode === 'day' && (
              <>
                {cameFromMonth && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs gap-1 mr-2"
                    onClick={() => { setViewMode('month'); setCameFromMonth(false) }}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Month
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDay(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-neutral-600 min-w-[160px] text-center">
                  {toLocalDate(date)}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDay(1)} disabled={isToday}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {viewMode === 'week' && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateWeek(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-neutral-600 min-w-[160px] text-center">
                  {toWeekLabel(weekStart)}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateWeek(1)} disabled={isCurrentWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {viewMode === 'month' && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-neutral-600 min-w-[160px] text-center">
                  {toMonthLabel(month)}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateMonth(1)} disabled={isCurrentMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-3 sm:px-6">
        {viewMode === 'day' && <DayView date={date} refreshKey={hasNewData} />}
        {viewMode === 'week' && <WeeklyEnergyChart weekStart={weekStart} refreshKey={hasNewData} />}
        {viewMode === 'month' && (
          <MonthlyEnergyCalendar
            month={month}
            refreshKey={hasNewData}
            onDayClick={(dateStr) => {
              setDate(new Date(dateStr + 'T00:00:00'))
              setViewMode('day')
              setCameFromMonth(true)
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}
